import React, { useEffect, useMemo, useState } from 'react';
import { Pencil, Trash2, X } from 'lucide-react';
import {
    Company,
    CompanyOwner,
    INDUSTRY_OPTIONS,
    UpdateCompanyPayload,
} from 'types/crm/company';
import {
    useUpdateCompanyMutation,
    useDeleteCompanyMutation,
} from 'service/crm/hooks/useUpdateCompanyMutation';
import UserAvatar from 'components/common/UserAvatar';
import toast from 'react-hot-toast';
import ActionsDropdown from 'components/common/ActionsDropdown';
import FormInput from 'components/common/FormInput';
import CopyableText from 'components/common/CopyableText';
import TagSelect from 'components/common/TagSelect';
import { tagChipStyle } from 'types/crm/tag';
import WarningDialog from 'components/common/WarningDialog';
import PhoneInput from 'components/common/PhoneInput';
import CountryList from 'country-list-with-dial-code-and-flag';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { CustomSelect } from 'components/common/CustomSelect';
import { Form, Formik, FormikProps } from 'formik';
import { useCrmNavigation } from 'hooks/useCrmNavigation';
import InlineOwnerField from '../Dropdowns/InlineOwnerFiled';
import { useRecordAccess } from 'context/crm/RecordAccessContext';
import FormTextarea from 'components/common/FormTextArea';
import DeleteModal, { CascadeItem } from 'components/Modals/CrmDeleteModal';

interface CompanyDetailsCardProps {
    company: Company;
    owners: CompanyOwner[];
}

interface EditFormValues {
    name: string;
    industry: string;
    cityLocation: string;
    websiteUrl: string;
    phone: string;
    linkedInUrl: string;
    description: string;
    companyOwnerUuid: string;
    tags: string[];
}

const buildInitialForm = (company: Company): EditFormValues => ({
    name: company.name ?? '',
    industry: company.industry ?? '',
    cityLocation: company.cityLocation ?? '',
    websiteUrl: company.websiteUrl ?? '',
    phone: company.phone ?? '',
    linkedInUrl: company.linkedInUrl ?? '',
    description: company.description ?? '',
    companyOwnerUuid: company.owner?.uuid ?? '',
    tags: company.tags?.map((t) => t.name) ?? [],
});

// Default to India when there's no existing phone number / no match, per product requirement.
const DEFAULT_PHONE_CODE = '+91';

/** Static stand-in for an editable field when the user lacks permission. */
const ReadOnlyField: React.FC<{ label: string; value?: string | null }> = ({ label, value }) => (
    <div className="rounded-lg border border-primary-subtle bg-primary-surface px-3 py-2">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-neutral-medium">
            {label}
        </div>
        <div className="mt-0.5 text-sm text-neutral">{value || '—'}</div>
    </div>
);

const CompanyDetailsCard: React.FC<CompanyDetailsCardProps> = ({ company, owners }) => {
    /**
     * RBAC (section 18): a CRM User edits this company only when they own it, or
     * have an assigned Task or Note on it — encoded in `company.canEdit`. Admin
     * and Manager are unrestricted. Ownership reassignment stays Admin/Manager
     * only (sections 2, 3). Resolved by <RecordAccessProvider> on the page.
     */
    const { canEdit, canDelete, canReassignOwner } = useRecordAccess();

    const updateCompany = useUpdateCompanyMutation();
    const deleteCompany = useDeleteCompanyMutation();
    const { toCompanies } = useCrmNavigation();

    const [isEditing, setIsEditing] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState<Company | null>(null);

    // Section 23: access can disappear mid-session (Task/Note reassigned, Owner
    // changed). Collapse any open edit surface rather than leaving a dead form.
    useEffect(() => {
        if (!canEdit) {
            setIsEditing(false);
            setDeleteTarget(null);
        }
    }, [canEdit]);

    // --- Phone (country code + national number split, same approach as CreateContact) ---
    const countries = useMemo(() => CountryList.getAll(), []);
    const [phoneCode, setPhoneCode] = useState(DEFAULT_PHONE_CODE);
    const [phoneNumberVal, setPhoneNumberVal] = useState('');

    const syncPhoneFromValue = (rawNum: string | null | undefined) => {
        const value = rawNum || '';
        if (value) {
            const parsed = parsePhoneNumberFromString(value);
            if (parsed) {
                setPhoneCode(`+${parsed.countryCallingCode}`);
                setPhoneNumberVal(parsed.nationalNumber);
            } else {
                const matchedCountry = countries.find((c: any) => value.startsWith(c.dial_code));
                if (matchedCountry) {
                    setPhoneCode(matchedCountry.dial_code);
                    setPhoneNumberVal(value.slice(matchedCountry.dial_code.length));
                } else {
                    setPhoneNumberVal(value);
                }
            }
        } else {
            setPhoneCode(DEFAULT_PHONE_CODE);
            setPhoneNumberVal('');
        }
    };

    useEffect(() => {
        syncPhoneFromValue(company.phone);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [company.phone, countries]);

    const ownerOptions = useMemo(
        () => owners.map((o: CompanyOwner) => ({ value: o.uuid, label: o.name.trim() })),
        [owners],
    );

    const industryOptions = useMemo(() => {
        const current = company.industry?.trim();
        if (!current || INDUSTRY_OPTIONS.some((o) => o.value === current)) return INDUSTRY_OPTIONS;
        return [...INDUSTRY_OPTIONS, { value: current, label: current }];
    }, [company.industry]);

    /** PATCH a single field from the read view. Returns the promise so the
     *  inline field can roll back if the request fails. */
    const patchCompany = (patch: Partial<EditFormValues>) => {
        if (!canEdit) {
            const err = new Error('You do not have permission to update this company.');
            toast.error(err.message);
            return Promise.reject(err);
        }
        return updateCompany.mutateAsync({
            companyUuid: company.uuid,
            ...patch,
        } as UpdateCompanyPayload);
    };

    const handleEdit = () => {
        if (!canEdit) return;
        syncPhoneFromValue(company.phone);
        setIsEditing(true);
    };

    const handleDelete = () => {
        if (!canDelete) return;
        setDeleteTarget(company);
    };

    const handleConfirmDelete = () => {
        if (!deleteTarget || !canDelete) return;
        deleteCompany.mutate(deleteTarget.uuid, {
            onSuccess: () => {
                toast.success('Company deleted');
                setDeleteTarget(null);
                toCompanies();
            },
            onError: (err: any) => {
                toast.error(err?.message ?? 'Failed to delete company. Please try again.');
            },
        });
    };

    const handleCancelDelete = () => setDeleteTarget(null);

    /** Records destroyed alongside the company. Zero-count rows are omitted. */
    const deleteCascade = useMemo<CascadeItem[]>(() => {
        if (!deleteTarget) return [];

        const { name, notesCount, tasksCount, activitiesCount } = deleteTarget;

        return [
            { count: 1, label: `Company — ${name}` },
            ...(notesCount ? [{ count: notesCount, label: 'Note' }] : []),
            ...(tasksCount ? [{ count: tasksCount, label: 'Task' }] : []),
            ...(activitiesCount
                ? [{ count: activitiesCount, label: 'Logged activity', labelPlural: 'Logged activities' }]
                : []),
        ];
    }, [deleteTarget]);

    const handlePhoneCodeChange = (
        newCode: string,
        setFieldValue: FormikProps<EditFormValues>['setFieldValue'],
    ) => {
        setPhoneCode(newCode);
        setFieldValue('phone', phoneNumberVal ? `${newCode}${phoneNumberVal}` : '');
    };

    const handlePhoneChange = (
        e: React.ChangeEvent<HTMLInputElement>,
        setFieldValue: FormikProps<EditFormValues>['setFieldValue'],
    ) => {
        const cleaned = e.target.value.replace(/\D/g, '').slice(0, 11);
        setPhoneNumberVal(cleaned);
        setFieldValue('phone', cleaned ? `${phoneCode}${cleaned}` : '');
    };

    const handleSaveChanges = (values: EditFormValues) => {
        if (!canEdit) {
            toast.error('You do not have permission to update this company.');
            setIsEditing(false);
            return;
        }

        // Ownership is Admin/Manager only — never let a CRM User's form submit
        // carry an owner change, even if state was seeded with one.
        const { companyOwnerUuid, ...rest } = values;
        const payload = {
            companyUuid: company.uuid,
            ...rest,
            ...(canReassignOwner ? { companyOwnerUuid } : {}),
        } as UpdateCompanyPayload;

        updateCompany.mutate(payload, {
            onError: (err: any) => {
                toast.error(err?.message ?? 'Failed to update company. Please try again.');
            },
            onSuccess: () => {
                toast.success('Company updated');
                setIsEditing(false);
            },
        });
    };

    const rowActions = useMemo(() => {
        const actions: Array<{
            label: string;
            icon: React.ReactNode;
            variant?: 'danger';
            onClick: () => void;
        }> = [];

        if (canEdit) {
            actions.push({
                label: 'Edit Company',
                icon: <Pencil className="w-4 h-4" />,
                onClick: handleEdit,
            });
        }
        if (canDelete) {
            actions.push({
                label: 'Delete Company',
                icon: <Trash2 className="w-4 h-4" />,
                variant: 'danger',
                onClick: handleDelete,
            });
        }
        return actions;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canEdit, canDelete, company.uuid]);

    if (isEditing && canEdit) {
        return (
            <Formik<EditFormValues>
                key={company.uuid}
                initialValues={buildInitialForm(company)}
                enableReinitialize
                onSubmit={handleSaveChanges}
            >
                {({ values, handleChange, handleSubmit, setFieldValue, handleBlur }) => (
                    <Form
                        onSubmit={handleSubmit}
                        className="w-full h-full rounded-l-xl bg-primary border-r border-primary-subtle p-4 space-y-5 overflow-auto"
                    >
                        {/* Header */}
                        <div className="flex items-start justify-between">
                            <div className="flex items-center gap-3">
                                <UserAvatar
                                    name={company.name}
                                    profileImageURL={company.imageUrl ?? undefined}
                                    className="w-12 h-12 text-base rounded-full"
                                />
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    type="submit"
                                    disabled={updateCompany.isPending}
                                    className="px-3 py-1.5 rounded-lg text-sm font-medium text-white bg-gradient-to-r from-purple-500 to-blue-500 disabled:opacity-60"
                                >
                                    {updateCompany.isPending ? 'Saving...' : 'Save Changes'}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setIsEditing(false)}
                                    disabled={updateCompany.isPending}
                                    className="p-1.5 rounded-lg text-neutral-medium hover:text-neutral hover:bg-primary-subtle/50"
                                    aria-label="Cancel edit"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        </div>

                        {/* Name / Industry / Location */}
                        <div className="space-y-3">
                            <FormInput
                                label="Company Name"
                                name="name"
                                value={values.name}
                                placeholder="Company name"
                                onChange={handleChange}
                                darkBg
                                className={{ input: 'text-sm px-3 h-[40px] placeholder:text-sm', label: 'text-xs ' }}
                            />
                            <CustomSelect
                                label="Industry"
                                name="industry"
                                options={industryOptions}
                                onBlur={handleBlur}
                                placeholder="Select industry"
                                darkBg
                                className={{
                                    root: 'flex-1 min-w-0',
                                    input: 'px-3 h-[40px]',
                                    value: 'text-sm',
                                    label: 'text-xs ',
                                }}
                            />

                            <FormInput
                                label="Location"
                                name="cityLocation"
                                value={values.cityLocation}
                                placeholder="City, State, Country"
                                onChange={handleChange}
                                darkBg
                                className={{ input: 'text-sm px-3 h-[40px] placeholder:text-sm', label: 'text-xs ' }}
                            />
                        </div>

                        {/* Contact info */}
                        <div className="overflow-visible">
                            <div className="space-y-3">
                                <FormInput
                                    label="Website"
                                    name="websiteUrl"
                                    value={values.websiteUrl}
                                    placeholder="https://company.com"
                                    onChange={handleChange}
                                    darkBg
                                    className={{ input: 'text-sm px-3 h-[40px] placeholder:text-sm', label: 'text-xs ' }}
                                />
                                <div className="relative z-20">
                                    <PhoneInput
                                        label="Phone"
                                        name="phone"
                                        value={phoneNumberVal}
                                        code={phoneCode}
                                        data={countries}
                                        setCode={(code) => handlePhoneCodeChange(code, setFieldValue)}
                                        onChange={(e) => handlePhoneChange(e, setFieldValue)}
                                        placeholder="Enter phone number"
                                        darkBg={true}
                                        className={{
                                            input: 'h-[40px]',
                                            field: 'text-sm px-3 placeholder:text-sm',
                                            label: 'text-xs text-neutral-medium',
                                        }}
                                    />
                                </div>
                                <FormInput
                                    label="LinkedIn"
                                    name="linkedInUrl"
                                    value={values.linkedInUrl}
                                    placeholder="linkedin.com/company/..."
                                    onChange={handleChange}
                                    darkBg
                                    className={{ input: 'text-sm px-3 h-[40px] placeholder:text-sm', label: 'text-xs ' }}
                                />
                            </div>
                        </div>

                        {/* Tags */}
                        <div>
                            <label className="text-xs text-[#9ca3af] font-medium block mb-2">Tags</label>
                            <TagSelect
                                value={values.tags}
                                onChange={(tags) => setFieldValue('tags', tags)}
                                recordType="COMPANY"
                                currentTags={company.tags}
                            />
                        </div>

                        {/* Owner — Admin/Manager may reassign; everyone else read-only */}
                        {canReassignOwner ? (
                            <CustomSelect
                                label="Owner"
                                name="companyOwnerUuid"
                                options={ownerOptions}
                                onBlur={handleBlur}
                                placeholder="Select owner"
                                darkBg
                                className={{ input: 'px-3 h-[40px]', value: 'text-sm', label: 'text-xs ' }}
                            />
                        ) : (
                            <ReadOnlyField label="Owner" value={company.owner?.name} />
                        )}

                        {/* Notes */}

                        <FormTextarea
                            name="description"
                            label="Insights"
                            placeholder="Add insights about this contact"
                            maxLength={500}
                            // darkBg
                            className={{ label: 'text-sm', textarea: 'text-sm' }}
                        />
                    </Form>
                )}
            </Formik>
        );
    }

    return (
        <div className="w-full h-full rounded-l-xl bg-primary border-r border-primary-subtle p-4 space-y-5 overflow-auto">
            {/* Header */}
            <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                    <UserAvatar
                        name={company.name}
                        profileImageURL={company.imageUrl ?? undefined}
                        className="w-12 h-12 text-base rounded-full"
                    />
                    <div>
                        <h3 className="text-base font-semibold text-neutral capitalize">{company.name}</h3>
                        <p className="text-xs text-neutral-medium mt-0.5 capitalize">
                            {[company.industry, company.cityLocation].filter(Boolean).join(' · ')}
                        </p>
                    </div>
                </div>
                {rowActions.length > 0 && <ActionsDropdown actions={rowActions} />}
            </div>

            {/* Contact info — always visible (section 4) */}
            <div className="overflow-visible">
                <div className="text-[11px] font-medium tracking-wide uppercase text-neutral-medium pt-2 pb-2">
                    Contact Info
                </div>
                <div className="rounded-lg border border-primary-subtle bg-primary-surface">
                    {[
                        { label: 'Website', value: company.websiteUrl, isLink: true, copyable: true },
                        {
                            label: 'Phone',
                            value: company.phoneNumber
                                ? `${company.countryCode ? (company.countryCode.startsWith('+') ? company.countryCode : `+${company.countryCode}`) + ' ' : ''}${company.phoneNumber}`
                                : null,
                            copyable: true,
                        },
                        { label: 'LinkedIn', value: company.linkedInUrl, isLink: true, copyable: true },
                    ].map((row) => (
                        <div
                            key={row.label}
                            className="px-3 py-2 border-t border-primary-subtle first:border-t-0"
                        >
                            <div className="text-[10px] font-semibold uppercase tracking-wider text-neutral-medium">
                                {row.label}
                            </div>
                            {row.copyable ? (
                                <CopyableText
                                    value={row.value ?? undefined}
                                    className="mt-0.5"
                                    textClassName={`text-sm ${row.isLink ? 'text-blue-400' : 'text-neutral'}`}
                                    truncate
                                />
                            ) : (
                                <div className="text-sm mt-0.5 text-neutral">{row.value || '—'}</div>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* Tags */}
            <div>
                <div className="text-[11px] tracking-wider font-semibold uppercase text-neutral-medium pb-2">
                    Tags
                </div>
                {company.tags && company.tags.length > 0 ? (
                    <div className="flex flex-wrap border border-primary-subtle py-3 px-3 rounded-lg bg-primary-surface gap-1.5">
                        {company.tags.map((tag) => (
                            <span
                                key={tag.name}
                                style={tagChipStyle(tag.colorKey)}
                                className="rounded-full border px-2.5 py-1 text-xs font-medium"
                            >
                                {tag.name}
                            </span>
                        ))}
                    </div>
                ) : (
                    <div className="text-sm text-neutral-medium">—</div>
                )}
            </div>

            {/* Owner — PATCH on select, Admin/Manager only */}
            <InlineOwnerField
                label="Owner"
                owners={owners}
                value={company.owner?.uuid ?? null}
                editable={canReassignOwner}
                onSave={(ownerUuid: any) => patchCompany({ companyOwnerUuid: ownerUuid ?? '' })}
                errorMessage="Could not reassign this company. Please try again."
            />

            {/* Notes */}

            <div>
                <div className="text-[11px] font-medium tracking-wide uppercase text-neutral-medium pt-2 pb-2">
                    Insights
                </div>
                <div className="py-2 px-3 rounded-lg bg-primary-surface border border-primary-subtle max-h-32 overflow-y-auto">
                    <p className="text-sm text-neutral-faded whitespace-pre-wrap [overflow-wrap:anywhere]">
                        {company.description || '—'}
                    </p>
                </div>
            </div>

            <DeleteModal
                isOpen={!!deleteTarget}
                onClose={handleCancelDelete}
                onConfirm={handleConfirmDelete}
                isDeleting={deleteCompany.isPending}
                entityLabel="Company"
                entityName={deleteTarget?.name}
                description={
                    deleteTarget ? (
                        <>
                            <strong className="text-white font-semibold">{deleteTarget.name}</strong> will
                            be permanently deleted from your CRM.
                        </>
                    ) : undefined
                }
                cascade={deleteCascade}
                preserved="Contacts and Deals linked to this company will remain in your CRM — only their association with this company is removed."
            />
        </div>
    );
};

export default CompanyDetailsCard;